import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, ClipboardList, ChefHat, Package,
  UtensilsCrossed, CalendarDays, Tag, BellRing, Users, ToggleRight,
  LogOut, Image as ImageIcon, Sparkles,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatures } from "@/contexts/FeatureContext";
import { Toaster } from "@/components/ui/sonner";
import BrandLogo from "@/components/BrandLogo";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, key: null, end: true },
  { to: "/admin/pos", label: "POS", icon: ShoppingCart, key: "pos" },
  { to: "/admin/pos-settings", label: "POS Settings", icon: ToggleRight, key: null, roles: ["owner","admin"] },
  { to: "/admin/orders", label: "Orders", icon: ClipboardList, key: null },
  { to: "/admin/kitchen", label: "Kitchen Display", icon: ChefHat, key: "kitchen_display" },
  { to: "/admin/picking", label: "Grocery Picking", icon: Package, key: "grocery_picking" },
  { to: "/admin/menu", label: "Restaurant Menu", icon: UtensilsCrossed, key: "restaurant_ordering" },
  { to: "/admin/daily-menu", label: "Daily Menu", icon: CalendarDays, key: "daily_menu" },
  { to: "/admin/products", label: "Grocery Products", icon: Package, key: "grocery_ordering" },
  { to: "/admin/promotions", label: "Promotions", icon: Tag, key: "promotions" },
  { to: "/admin/popups", label: "Popup Ads", icon: BellRing, key: "popup_ads" },
  { to: "/admin/customers", label: "Customers", icon: Users, key: null, roles: ["owner","admin"] },
  { to: "/admin/lechon", label: "Lechon Reservations", icon: Sparkles, key: "lechon_reservation" },
  { to: "/admin/catering", label: "Catering", icon: ImageIcon, key: "catering" },
  { to: "/admin/branding", label: "Branding", icon: ImageIcon, key: null, roles: ["owner","admin","marketing"] },
  { to: "/admin/features", label: "Feature Controls", icon: ToggleRight, key: null, roles: ["owner","admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { isOn } = useFeatures();
  const navigate = useNavigate();

  if (!user) { navigate("/login"); return null; }
  if (user.role === "customer") { navigate("/account"); return null; }

  const visible = items.filter((it) => {
    if (it.roles && !it.roles.includes(user.role) && user.role !== "owner") return false;
    return true;
  });

  return (
    <div className="min-h-screen flex admin-bg">
      <aside className="hidden lg:block w-64 admin-sidebar p-5 sticky top-0 h-screen overflow-y-auto" data-testid="admin-sidebar">
        <BrandLogo type="admin" label="Maharlika" sublabel="Control Panel" size="sm" className="mb-7" />
        <nav className="space-y-1">
          {visible.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#D4AF37]/15 text-[#D4AF37] border-l-2 border-[#D4AF37]" : "text-[#FAF9F6]/70 hover:bg-white/5 hover:text-[#D4AF37]"
                }`
              }
            >
              <it.icon className="w-4 h-4" strokeWidth={1.5} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8 pt-4 border-t border-[#D4AF37]/15">
          <div className="text-xs text-[#8A8A8A] mb-2">{user.email}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-3">{user.role}</div>
          <button
            data-testid="admin-logout"
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 text-sm text-[#FAF9F6]/70 hover:text-[#D4AF37]"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 admin-sidebar overflow-x-auto no-scrollbar flex">
        {visible.slice(0, 5).map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} className={({ isActive }) =>
            `flex-1 min-w-[80px] py-3 flex flex-col items-center gap-1 text-[10px] ${isActive ? "text-[#D4AF37]" : "text-[#FAF9F6]/60"}`
          }>
            <it.icon className="w-4 h-4" />
            {it.label.split(" ")[0]}
          </NavLink>
        ))}
      </div>

      <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8 overflow-x-hidden">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
